import type { Base } from 'active-resource';
import type { GConstructor } from './APILibrary';
import APILibrary, { ClassExtender, GenerateResource } from './APILibrary';
import isBoolean from 'lodash/isBoolean';
import format from 'date-fns/format';
import { normalizeAbsoluteDate } from 'util/formatting/dates';

/**
 * Relationship classes
 */
export class WorkHistoryAttributes {
  aiGenerated: boolean = null;
  approaches: string[] = null;
  city: string = null;
  company: string = null;
  current: boolean = null;
  description: string = null;
  endDate: string = null;
  gradeLevels: string[] = null;
  id: string = null;
  jobTitle: string = null;
  languages: string[] = null;
  startDate: string = null;
  teachingRoles: string[] = null;
  teachingSettings: string[] = null;
  workedWithChildren: boolean = null;
}

export class WorkHistoryBelongsTo {}
export class WorkHistoryHasOne {}
export class WorkHistoryHasMany {}

/**
 * Utility interfaces
 */
export interface WorkHistoryMethods {
  areDatesValid: () => boolean;
  isResourceValid: () => boolean;
  getExperienceDurationText: () => string;
}

export interface WorkHistoryInterface extends WorkHistoryAttributes, WorkHistoryType {}

type WorkHistoryType = GConstructor<WorkHistoryInterface>;

export interface WorkHistoryResource
  extends Base<WorkHistoryAttributes>,
    WorkHistoryInterface,
    WorkHistoryMethods {}

/**
 * Resource methods
 */
const BaseClass = () =>
  GenerateResource<WorkHistoryAttributes>({
    queryName: 'user_profile/work_histories',
    className: 'WorkHistory',
    AttributesClass: WorkHistoryAttributes,
  });

export const WorkHistoryResourceClass = <TBase extends WorkHistoryType>(
  BaseConstructor: TBase,
): TBase =>
  class extends BaseConstructor implements WorkHistoryMethods {
    areDatesValid() {
      return this.startDate && ((this.endDate && this.startDate <= this.endDate) || this.current);
    }

    isResourceValid() {
      return (
        this.areDatesValid() &&
        this.company &&
        this.jobTitle &&
        this.city &&
        isBoolean(this.workedWithChildren)
      );
    }

    getExperienceDurationText() {
      if (!this.startDate || (!this.current && !this.endDate)) {
        return '';
      }

      const formattedStartDate = format(normalizeAbsoluteDate(new Date(this.startDate)), 'MMM yy');

      if (this.current) {
        return `${formattedStartDate} - Present`;
      }

      const formattedEndDate = format(normalizeAbsoluteDate(new Date(this.endDate)), 'MMM yy');

      if (formattedStartDate === formattedEndDate) {
        return formattedStartDate;
      }

      return `${formattedStartDate} - ${formattedEndDate}`;
    }
  };

export class WorkHistoryResource extends ClassExtender<WorkHistoryInterface>(
  BaseClass,
  WorkHistoryResourceClass,
) {}

/**
 * Resource instance
 */
export const WorkHistory = APILibrary.createResource<WorkHistoryResource>(WorkHistoryResource);
